import { createLogic } from "redux-logic";
import * as DOMUtils from "history/DOMUtils";
import {
    type NewCarCompareChangeColourType,
    type NewCarCompareGetEtaType,
    type NewCarCompareSetCarDataType,
    type NewCarCompareSetCarToAddType,
    type NewCarCompareSetNewEngineType,
    type NewCarCompareLoadFinanceType,
    NEW_CAR_COMPARE_CHANGE_COLOUR,
    NEW_CAR_COMPARE_GET_ETA,
    NEW_CAR_COMPARE_SET_CAR_DATA,
    NEW_CAR_COMPARE_SET_CAR_TO_ADD,
    NEW_CAR_COMPARE_SET_NEW_ENGINE,
    compareGetEta,
    compareSetCarData,
    compareSetCarToAdd,
    compareSetEta,
    compareSetParsedData,
    compareSetFinance,
    NEW_CAR_COMPARE_LOAD_FINANCE,
    compareLoadFinance,
} from "../actions/NewCarCompareActions";
import Debug from "../../../../common-deprecated/Debug";
import {
    getSpecsForSelectedEngines,
    parseEquipment,
    parsePacks,
    parsePacksLeasing,
    parseSpecs,
    parseTitlesOfEquipment,
    parseTitlesOfPacks,
    parseTitlesOfSpecs,
} from "../../utils/formatters/equipmentAndSpecsFormatters";
import type { SetCarDataType } from "../../utils/compareTypes";
import type { CarFinanceDataType } from "../../../../common-deprecated/features/compare/utils/newCarCompareTypes";
import type { CompareV2LogicType } from "../store";
import {
    getCarModelColourUpholsteryIds,
    getSingleCarCashPriceAndId,
} from "../../utils/formatters/priceAndEcoSpecsFormatters";
import { getColourCode, mapPossibleModelIdToCode } from "../../utils/utils";
import { gradesToCarIdsType, setCompareNewCarsDataLocalStorage } from "../../utils/localStorage";
import { getMaxNumberOfCarsToCompare } from "../../../../shared-logic/features/compare/utils/helpers";
import { getNameFromModelGradeBodyTypeIdsAndAvailabelCars } from "../../utils/formatters/nameFormatters";
import getTheme from "../../../../common-deprecated/themes/getTheme";
import { showModal } from "../../../../common-deprecated/redux/actions/ModalActions";
import type { CompareV2RequestBodyType } from "../../server/data/fetchCompareV2";
import { getAPI } from "../../../../common-deprecated/settings/utils/commonSettingUtils";
import { loadEtaValuesInCompare } from "../../utils/settings";
import { MODAL_REPLACE_CAR } from "../../../../shared-logic/features/compare/utils/modalConstants";
import { shouldShowErrorModal } from "../../../../common-deprecated/utils";
import { defaultFinanceInfoObject } from "../../../../common-deprecated/constants";
import type { FinanceServiceDataType } from "../../../../common-deprecated/server/data/FinanceDataTypes";
import type { EtaEndpointReturnType } from "../../../shared-logic/server/api/EtaEndpoint";
import { addErrorLog, addRequestLog } from "../../../../common-deprecated/redux/actions/UtilActions";
import { MODAL_ERRORS } from "../../../../common-deprecated/utils/modalConstants";
import { COMPONENT_COMPARE_V2 } from "../../../../shared-logic/server/components";
import { showAemMonthlyRates } from "../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import { AemFmComponent } from "../../../../common-deprecated/utils/aemFlexibilityMatrixUtils";
import {
    type NewCarCompareAddCarType,
    type NewCarCompareClearAllType,
    type NewCarCompareRemoveCarType,
    type NewCarCompareSetFinanceProductType,
    compareClearAll,
    NEW_CAR_COMPARE_ADD_CAR,
    NEW_CAR_COMPARE_CLEAR_ALL,
    NEW_CAR_COMPARE_REMOVE_CAR,
    NEW_CAR_COMPARE_SET_FINANCE_PRODUCT,
} from "../../../../common-deprecated/features/compare/redux/actions/NewCarCompareExternalDataActions";
import { NcCompareContext } from "../../../../common-deprecated/types/NewCarCompareTypes";
import { sanitize } from "../../../../common-deprecated/utils/sanitizer";
import type { QueryType } from "../../../../common-deprecated/server/types";

const changeColourLogic = (createLogic as CompareV2LogicType<NewCarCompareChangeColourType>)({
    type: [NEW_CAR_COMPARE_CHANGE_COLOUR],
    async process({ getState }, _, done): Promise<void> {
        const { newCarCompare, commonSettings } = getState();

        setCompareNewCarsDataLocalStorage(
            commonSettings,
            {
                colourIds: newCarCompare.grades.map((grade) => grade.colourCode),
            },
            newCarCompare.ncCompareContext,
        );
        done();
    },
});

const startComparisonLogic = (createLogic as CompareV2LogicType<NewCarCompareAddCarType>)({
    type: [NEW_CAR_COMPARE_ADD_CAR],

    transform({ getState, action }, next, reject) {
        const { newCarCompare, compareV2Settings, commonSettings } = getState();
        const { modelGradeBodyTypeIds } = action;
        const existingData = action.data;
        const { compareV2Standalone } = compareV2Settings;
        // using the grade, model, bodytypeIds we fetch the name from the available cars list.
        // this name will be set as a placeholder while the comparedata is being loaded.
        const modelName = getNameFromModelGradeBodyTypeIdsAndAvailabelCars(
            modelGradeBodyTypeIds,
            newCarCompare.available,
        );

        const data = { ...existingData, loading: true, modelName };

        const newIndex = action.index === -1 ? newCarCompare.grades.length : action.index;

        const { breakpoints } = getTheme(commonSettings);

        if (newIndex < getMaxNumberOfCarsToCompare(breakpoints, compareV2Standalone)) {
            next({ ...action, data, index: newIndex } as any);
        } else {
            reject(compareSetCarToAdd(modelGradeBodyTypeIds));
        }
    },
    process: async ({ getState, action }, dispatch, done): Promise<void> => {
        try {
            const { commonSettings, newCarCompare } = getState();
            const { modelGradeBodyTypeIds, index } = action;

            const body: CompareV2RequestBodyType = {
                modelCode: mapPossibleModelIdToCode(modelGradeBodyTypeIds, newCarCompare.available, commonSettings),
                submodelId: modelGradeBodyTypeIds.submodelId,
                gradeId: modelGradeBodyTypeIds.gradeId,
                bodyTypeId: modelGradeBodyTypeIds.bodyTypeId,
                isStandalone: commonSettings.isStandalone,
            };
            // Add configurationId for leasing compare API request body
            if (modelGradeBodyTypeIds.configurationId) {
                body.configurationId = modelGradeBodyTypeIds.configurationId;
            }

            // TODO: move this to datafetch logic
            // Component Environment has to be added with these client side fetches.
            const url = getAPI(commonSettings, "compare-v2");
            const request = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });

            const data: SetCarDataType = await request.json();
            if (data.error || data.errorMessage) {
                dispatch(compareClearAll());
                throw new Error(data.error);
            }

            // If an engine is passed along, we override the default engine.
            if (action.motorizationIds) {
                const correctEngine = data.enginesWithSpecs.find(
                    (engine) =>
                        action.motorizationIds &&
                        engine.engineId === action.motorizationIds.engineId &&
                        engine.transmissionId === action.motorizationIds.transmissionId &&
                        engine.wheeldriveId === action.motorizationIds.wheeldriveId,
                );

                if (correctEngine) {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { specs, ...selectedEngine } = correctEngine;
                    data.selectedEngine = selectedEngine;
                }
            }

            //Check if we are passed a colourId, if so set this
            if (action.colourCode) {
                const { colourCode } = action;
                data.carData.colourCode = getColourCode(colourCode, data.carData.availableColours);
            }

            dispatch(compareSetCarData(data, index));
        } catch (ex) {
            Debug.error("Error while executing compare request", ex);
        }
        done();
    },
});

type ParseActionType = NewCarCompareSetCarDataType | NewCarCompareSetNewEngineType | NewCarCompareRemoveCarType;
const parseCompareData = (createLogic as CompareV2LogicType<ParseActionType>)({
    // Parse all the data when we select a new car, or change the engine.
    type: [NEW_CAR_COMPARE_SET_CAR_DATA, NEW_CAR_COMPARE_SET_NEW_ENGINE, NEW_CAR_COMPARE_REMOVE_CAR],
    async process({ getState }, dispatch, done): Promise<void> {
        const { newCarCompare, commonSettings } = getState();

        if (!newCarCompare.grades.filter((grade) => !!grade && grade.loading).length) {
            if (DOMUtils.canUseDOM) {
                setCompareNewCarsDataLocalStorage(
                    commonSettings,
                    {
                        carIds: gradesToCarIdsType(newCarCompare.grades),
                        motorizationIds: newCarCompare.selectedEngines,
                        colourIds: newCarCompare.grades.map((grade) => grade.colourCode),
                    },
                    newCarCompare.ncCompareContext,
                );
            }

            const carIds = newCarCompare.grades.map((grade, index) => {
                return getCarModelColourUpholsteryIds(grade, newCarCompare.selectedEngines[index]).carId;
            });

            const parsedTitlesOfSpecs = parseTitlesOfSpecs(newCarCompare.specList);
            const preParsedSpecs = getSpecsForSelectedEngines(newCarCompare.specs, newCarCompare.selectedEngines);
            const parsedSpecs = parseSpecs(parsedTitlesOfSpecs, preParsedSpecs, commonSettings);

            const parsedTitlesOfPacks = parseTitlesOfPacks(newCarCompare.packsList);
            const isLeasing = newCarCompare.ncCompareContext === NcCompareContext.Leasing;
            const parsedPacks = isLeasing
                ? parsePacksLeasing(newCarCompare.grades)
                : parsePacks(newCarCompare.packs, parsedTitlesOfPacks, carIds);

            const parsedTitlesOfEquipment = parseTitlesOfEquipment(newCarCompare.equipmentList);
            const parsedEquipment = parseEquipment(
                newCarCompare.ncCompareContext,
                newCarCompare.equipment,
                parsedTitlesOfEquipment,
                parsedPacks,
                carIds,
            );

            dispatch(compareSetParsedData(parsedSpecs, parsedEquipment, parsedPacks));
        }

        done();
    },
});

const clearAllLogic = (
    createLogic as CompareV2LogicType<NewCarCompareClearAllType | NewCarCompareSetFinanceProductType>
)({
    type: [NEW_CAR_COMPARE_CLEAR_ALL, NEW_CAR_COMPARE_SET_FINANCE_PRODUCT],
    process({ getState }) {
        const { commonSettings, newCarCompare } = getState();

        setCompareNewCarsDataLocalStorage(
            commonSettings,
            {
                carIds: [],
                motorizationIds: [],
                colourIds: [],
            },
            newCarCompare.ncCompareContext,
        );
    },
});

const getEtaData = (createLogic as CompareV2LogicType<NewCarCompareSetCarDataType | NewCarCompareSetNewEngineType>)({
    type: [NEW_CAR_COMPARE_SET_CAR_DATA, NEW_CAR_COMPARE_SET_NEW_ENGINE],
    validate({ getState, action }, allow, reject) {
        const { compareV2Settings } = getState();
        if (loadEtaValuesInCompare(compareV2Settings)) {
            allow(action);
        } else {
            reject(action);
        }
    },
    async process({ getState, action }, dispatch, done): Promise<void> {
        const { newCarCompare } = getState();
        const { index } = action;

        const { modelId, carId, colourId, upholsteryId } = getCarModelColourUpholsteryIds(
            newCarCompare.grades[index],
            newCarCompare.selectedEngines[index],
        );
        dispatch(compareGetEta(modelId, carId, index, colourId, upholsteryId));
        done();
    },
});

const setEtaData = (createLogic as CompareV2LogicType<NewCarCompareGetEtaType>)({
    type: [NEW_CAR_COMPARE_GET_ETA],
    async process({ getState, action }, dispatch, done): Promise<void> {
        try {
            const { commonSettings } = getState();

            if (!action.modelId || !action.carId) {
                dispatch(compareSetEta(action.index, "", "no Model or Car ID"));
                done();
                return;
            }
            const additionalQueries = new Map<keyof QueryType, unknown>([["onlyDate", true]]);
            const etaEndpoint = `${getAPI(commonSettings, "eta", `/${action.modelId}/${action.carId}`, additionalQueries)}`;

            const etaResult = await fetch(etaEndpoint, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            const etaConfig: EtaEndpointReturnType = await etaResult.json();

            const { fetchResult, reqLogs, errorLogs } = etaConfig;

            if (errorLogs?.length) {
                dispatch(addErrorLog(errorLogs));

                if (shouldShowErrorModal(commonSettings, errorLogs)) {
                    const modalSettings = {
                        type: MODAL_ERRORS,
                        errors: errorLogs,
                        componentName: COMPONENT_COMPARE_V2,
                    };
                    dispatch(showModal(modalSettings));
                }
            }

            if (reqLogs.length) dispatch(addRequestLog(reqLogs));

            dispatch(compareSetEta(action.index, fetchResult.date, ""));
        } catch (ex) {
            Debug.error("Error while executing get ETA (only date) request", ex);
        }
        done();
    },
});

const replaceCarLogic = (createLogic as CompareV2LogicType<NewCarCompareSetCarToAddType>)({
    type: [NEW_CAR_COMPARE_SET_CAR_TO_ADD],
    process({ action }, dispatch, done): void {
        dispatch(
            showModal({
                type: MODAL_REPLACE_CAR,
                // TODO: when replacing a new car only basic ids will be taken into account, motorization and colourCodes get stripped
                newCarToAdd: action.modelGradeBodyTypeIds,
            }),
        );
        done();
    },
});

/**
 * Starts loading the finance quote when a car was loaded and when changing colour or engine
 */
const loadFinanceLogic = (
    createLogic as CompareV2LogicType<
        NewCarCompareSetCarDataType | NewCarCompareChangeColourType | NewCarCompareSetNewEngineType
    >
)({
    type: [NEW_CAR_COMPARE_SET_CAR_DATA, NEW_CAR_COMPARE_CHANGE_COLOUR, NEW_CAR_COMPARE_SET_NEW_ENGINE],
    process({ getState, action }, dispatch, done): void {
        const { index } = action;
        const { commonSettings } = getState();

        if (showAemMonthlyRates(commonSettings, AemFmComponent.Compare, "new")) {
            dispatch(compareLoadFinance(index));
        }

        done();
    },
});

/**
 * Fetches the finance quote
 */
const setFinanceLogic = (createLogic as CompareV2LogicType<NewCarCompareLoadFinanceType>)({
    type: [NEW_CAR_COMPARE_LOAD_FINANCE],
    async process({ getState, action }, dispatch, done): Promise<void> {
        const { newCarCompare, compareV2Settings, commonSettings } = getState();
        const { index } = action;

        // look for the entire colour object as only the selected code is saved
        const currentColour = newCarCompare.grades[index].availableColours.find(
            (colour) => colour.code === newCarCompare.grades[index].colourCode,
        );
        const { carId, price, discount } = getSingleCarCashPriceAndId(
            newCarCompare.grades[index],
            newCarCompare.selectedEngines[index],
        );

        const body = {
            Country: commonSettings.country,
            Brand: commonSettings.brand,
            Language: commonSettings.language,
            ModelID: newCarCompare.grades[index].modelId,
            CarID: carId,
            ExteriorColourID: currentColour?.id,
            TotalPrice: discount || price,
        };

        // See https://finance-prev.toyota-europe.com/api-docs/#/tfs/post_tfs_cars
        const url = compareV2Settings.financeGateway;
        const financeRequest = await fetch(url, {
            method: "POST",
            body: JSON.stringify({ car: body }),
            headers: {
                "Content-Type": "application/json",
            },
        });
        const financeData: FinanceServiceDataType = await financeRequest.json();

        if (financeData?.car?.rate) {
            const data: CarFinanceDataType = {
                rate: Number(financeData.car.rate.monthlyPayment?.value || "0"),
                loading: false,
                financeInfo: {
                    ...defaultFinanceInfoObject,
                    productName: financeData.car.rate.productName?.formatted || "",
                    term: financeData.car.rate.term?.formatted || "",
                    disclaimer: {
                        value: financeData.car.rate.quote.value ? sanitize(financeData.car.rate.quote.value) : "",
                        table: financeData.car.rate.quote.calculations,
                    },
                },
            };
            dispatch(compareSetFinance(data, index));
        } else {
            dispatch(compareSetFinance({ loading: false, rate: 0, financeInfo: defaultFinanceInfoObject }, index));
        }

        done();
    },
});

const newCarCompareLogic = [
    replaceCarLogic,
    startComparisonLogic,
    parseCompareData,
    getEtaData,
    setEtaData,
    clearAllLogic,
    changeColourLogic,
    loadFinanceLogic,
    setFinanceLogic,
];
export default newCarCompareLogic;
export const serverNewCarCompareLogic = [startComparisonLogic, parseCompareData];
